import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Music, Users, DollarSign, ShoppingCart, LogOut, Palette, Download, Loader2, Key, CreditCard, CheckCircle2, AlertTriangle, ExternalLink, Copy, Check, GraduationCap, Radio, UserCog, Settings, ChevronRight, Mail, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import { adminFetch, isAdminAuthenticated, adminLogout, getAdminUsername, isAdmin } from '@/utils/adminAuth';
import { toast } from 'sonner';
import { DashboardTile } from '@/components/admin/DashboardTile';
import { quickActionsTiles, liveGalleryTiles, mediaAndEditorsTiles, analyticsTiles } from './adminDashboardTiles';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [copiedInstructions, setCopiedInstructions] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Deep-link: when URL has a hash (e.g. #class-editor-btn), jump directly
  // to that dashboard card and briefly flash a highlight ring on it.
  useEffect(() => {
    if (!location.hash || loading) return;
    const id = location.hash.replace('#', '');
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'auto', block: 'center' });
    el.classList.add('ring-4', 'ring-primary', 'ring-offset-2', 'ring-offset-background');
    const timer = setTimeout(() => {
      el.classList.remove('ring-4', 'ring-primary', 'ring-offset-2', 'ring-offset-background');
    }, 2500);
    return () => clearTimeout(timer);
  }, [location.hash, loading]);

  const copyDeployInstructions = () => {
    const instructions = `How to Update API Keys in Emergent:

1. Go to Emergent platform (https://emergentagent.com)
2. Click "Home" tab
3. Find your app under "Deployed Apps"
4. Click your app → "Deployment Settings" or gear icon
5. Look for "Environment Variables" section
6. Update these keys:
   - STRIPE_API_KEY = your Stripe live key (starts with sk_live_)
   - PAYPAL_CLIENT_ID = your PayPal client ID
   - PAYPAL_SECRET = your PayPal secret
   - RESEND_API_KEY = your Resend API key
7. Click "Save" then "Redeploy"

Get your keys from:
- Stripe: https://dashboard.stripe.com/apikeys
- PayPal: https://developer.paypal.com/dashboard/applications
- Resend: https://resend.com/api-keys`;

    navigator.clipboard.writeText(instructions);
    setCopiedInstructions(true);
    toast.success('Instructions copied to clipboard!');
    setTimeout(() => setCopiedInstructions(false), 3000);
  };

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await adminFetch('/admin/dashboard/stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const importMusicCatalog = async () => {
    if (!confirm('This will import all 5 albums (57 tracks) from the Escola de Samba Casa Samba legacy site. This will replace any existing music. Continue?')) {
      return;
    }
    
    setImporting(true);
    try {
      const response = await adminFetch('/admin/import-music-catalog', {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Imported ${data.albums} albums and ${data.tracks} tracks!`);
        loadStats(); // Refresh stats
      } else {
        toast.error('Failed to import music catalog');
      }
    } catch (error) {
      console.error('Error importing music:', error);
      toast.error('Failed to import music catalog');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Revenue',
      value: `$${stats?.total_revenue || 0}`,
      icon: DollarSign,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Total Orders',
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      color: 'from-primary to-accent',
      bgColor: 'bg-primary/10',
    },
    {
      title: 'Tracks',
      value: stats?.total_tracks || 0,
      icon: Music,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Customers',
      value: stats?.total_customers || 0,
      icon: Users,
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-500/10',
    },
  ];

  return (
    <div className="min-h-screen bg-background noise-texture">
      {/* Header */}
      <header className="bg-card border-b border-border/50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-glow">
              <Music className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-2xl font-bold font-display text-foreground">
                Escola de Samba Casa Samba Admin
              </h1>
              <p className="text-sm text-muted-foreground">
                Welcome back, {getAdminUsername()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {window.location.hostname.endsWith('preview.emergentagent.com') ? (
              <span
                className="px-4 py-2 rounded-full bg-primary/20 border border-primary text-primary text-sm font-bold uppercase tracking-wider"
                data-testid="env-badge-preview"
              >
                Preview Workshop
              </span>
            ) : (
              <>
                <span
                  className="px-4 py-2 rounded-full bg-green-500/20 border border-green-500 text-green-400 text-sm font-bold uppercase tracking-wider"
                  data-testid="env-badge-live"
                >
                  Live Site
                </span>
                <a
                  href="https://casa-samba-music.preview.emergentagent.com/admin"
                  className="px-4 py-2 rounded-full bg-primary text-black text-sm font-bold hover:bg-primary/90 transition-colors"
                  data-testid="open-preview-workshop-btn"
                >
                  Open Preview Workshop →
                </a>
              </>
            )}
            <button
              onClick={adminLogout}
              className="flex items-center gap-2 px-4 py-2 text-muted-foreground hover:text-foreground border border-border hover:border-primary/50 rounded-full transition-all"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* DEPLOYMENT STATUS - Always visible at top */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/50 rounded-2xl p-6"
          data-testid="deployment-status-section"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <ExternalLink className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">Your Live Sites</h3>
              <p className="text-muted-foreground text-sm">
                Preview = testing • Production = your real customer site
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Production Site */}
            <div className="bg-card/50 border-2 border-green-500/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded">PRODUCTION</span>
                <span className="text-xs text-muted-foreground">← Your real customer site</span>
              </div>
              <a
                href="https://escoladesamba.casasamba.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-400 hover:text-green-300 font-mono text-sm break-all block mb-3"
              >
                escoladesamba.casasamba.com
              </a>
              <a
                href="https://escoladesamba.casasamba.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-all"
              >
                <ExternalLink className="w-4 h-4" />
                View Production Site
              </a>
            </div>

            {/* Preview Site */}
            <div className="bg-card/50 border border-border/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 bg-yellow-500 text-black text-xs font-bold rounded">PREVIEW</span>
                <span className="text-xs text-muted-foreground">← Testing only (not live)</span>
              </div>
              <a
                href={process.env.REACT_APP_BACKEND_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-yellow-400 hover:text-yellow-300 font-mono text-sm break-all block mb-3"
              >
                {process.env.REACT_APP_BACKEND_URL?.replace('https://', '')}
              </a>
              <a
                href={process.env.REACT_APP_BACKEND_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg transition-all"
              >
                <ExternalLink className="w-4 h-4" />
                View Preview Site
              </a>
            </div>
          </div>

          {/* Redeploy Instructions */}
          <div className="bg-card/80 border border-amber-500/50 rounded-xl p-4">
            <p className="text-foreground font-semibold mb-2">Changes not showing on Production?</p>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>1. Click <span className="text-primary font-semibold">Home</span> tab (top of Emergent)</p>
              <p>2. Find your app in <span className="text-primary font-semibold">Deployed Apps</span></p>
              <p>3. Click <span className="text-primary font-semibold">Redeploy</span> button</p>
              <p>4. Wait 10-15 minutes for deployment to complete</p>
            </div>
          </div>
        </motion.div>

        {/* Import Music Banner - One-Click Import */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border-2 border-orange-500/50 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground">Import Music Catalog</h3>
                <p className="text-muted-foreground text-sm">
                  One-click import of all 5 albums (57 tracks) from shop.casasamba.com
                </p>
              </div>
            </div>
            <button
              onClick={importMusicCatalog}
              disabled={importing}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-bold rounded-full transition-all disabled:opacity-50 flex items-center gap-2"
              data-testid="import-music-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Import All Music
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* Environment & API Keys Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-2xl p-6"
          data-testid="env-api-keys-section"
        >
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                <Key className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground">Environment & API Keys</h3>
                <p className="text-muted-foreground text-sm">
                  Quick reference for your payment & integration settings
                </p>
              </div>
            </div>
            <button
              onClick={copyDeployInstructions}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-full transition-all flex items-center gap-2"
              data-testid="copy-instructions-btn"
            >
              {copiedInstructions ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Instructions
                </>
              )}
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Stripe Status */}
            <a
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-card/50 border border-border/30 rounded-xl p-4 hover:border-purple-500/50 hover:bg-purple-500/10 transition-all cursor-pointer block"
              data-testid="stripe-dashboard-link"
            >
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className="w-5 h-5 text-purple-400" />
                <span className="font-semibold text-foreground">Stripe</span>
                <span className="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">
                  <CheckCircle2 className="w-3 h-3" /> Active
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Handles courses, memberships, lessons & services
              </p>
              <div className="text-xs text-primary flex items-center gap-1 font-semibold">
                <ExternalLink className="w-3 h-3" />
                Open Stripe Dashboard →
              </div>
            </a>

            {/* PayPal Status */}
            <a
              href="https://developer.paypal.com/dashboard/applications"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-card/50 border border-border/30 rounded-xl p-4 hover:border-blue-500/50 hover:bg-blue-500/10 transition-all cursor-pointer block"
              data-testid="paypal-dashboard-link"
            >
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className="w-5 h-5 text-blue-400" />
                <span className="font-semibold text-foreground">PayPal</span>
                <span className="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">
                  <AlertTriangle className="w-3 h-3" /> Verify
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Secondary payment option for one-time purchases
              </p>
              <div className="text-xs text-primary flex items-center gap-1 font-semibold">
                <ExternalLink className="w-3 h-3" />
                Open PayPal Developer →
              </div>
            </a>

            {/* How to Update - Now a button */}
            <div className="bg-card/50 border border-border/30 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <span className="font-semibold text-foreground">Update Production Keys</span>
              </div>
              <ol className="text-xs text-muted-foreground space-y-1 mb-3">
                <li>1. Go to Emergent → Home → Deployed Apps</li>
                <li>2. Click your app → Deployment Settings</li>
                <li>3. Update Environment Variables</li>
                <li>4. Click <span className="text-primary font-semibold">Redeploy</span></li>
              </ol>
              <a
                href="https://emergentagent.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary flex items-center gap-1 font-semibold hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                Go to Emergent Platform →
              </a>
            </div>
          </div>

          {/* Required Keys Reference */}
          <div className="mt-4 pt-4 border-t border-border/30">
            <p className="text-xs text-muted-foreground mb-2 font-semibold">Required Environment Variables for Production:</p>
            <div className="flex flex-wrap gap-2">
              {['STRIPE_API_KEY', 'PAYPAL_CLIENT_ID', 'PAYPAL_SECRET', 'RESEND_API_KEY'].map((key) => (
                <span key={key} className="text-xs px-2 py-1 bg-background rounded border border-border/50 text-muted-foreground font-mono">
                  {key}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`${stat.bgColor} border border-border/50 rounded-2xl p-6 hover:scale-105 transition-transform`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <p className="text-muted-foreground text-sm mb-1">{stat.title}</p>
                <p className="text-3xl font-bold font-display text-foreground">
                  {stat.value}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Site Manager Button - Main Access Point */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/admin/site-manager')}
            className="w-full bg-gradient-to-r from-primary to-primary/80 text-black rounded-2xl p-6 hover:opacity-90 transition-all text-left flex items-center justify-between group"
            data-testid="site-manager-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-black/20 flex items-center justify-center">
                <Settings className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-1">Site Manager</h3>
                <p className="text-black/70">Manage users, content, orders, and settings - all in one place</p>
              </div>
            </div>
            <ChevronRight className="w-8 h-8 group-hover:translate-x-2 transition-transform" />
          </button>
        </motion.div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {quickActionsTiles.map((t) => (
            <DashboardTile
              key={t.testId || t.to + t.title}
              {...t}
              onClick={() => navigate(t.to)}
            />
          ))}
        </div>

        {/* Test Purchase Notification — verify email pipeline without spending money */}
        <TestNotificationCard />

        {/* Live Lessons & Gallery Management */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {liveGalleryTiles.map((t) => (
            <DashboardTile
              key={t.testId || t.to + t.title}
              {...t}
              onClick={() => navigate(t.to)}
            />
          ))}
        </div>

        {/* Media Library & Editors */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {mediaAndEditorsTiles.map((t) => (
            <DashboardTile
              key={t.testId || t.to + t.title}
              {...t}
              onClick={() => navigate(t.to)}
            />
          ))}
        </div>

        {/* Analytics & QR Codes Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {analyticsTiles.map((t) => (
            <DashboardTile
              key={t.testId || t.to + t.title}
              {...t}
              onClick={() => navigate(t.to)}
            />
          ))}
        </div>

        {/* Live & Podcast Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.92 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/admin/live')}
            className="w-full bg-gradient-to-r from-red-500/20 to-purple-500/20 border-2 border-red-500/30 rounded-2xl p-6 hover:border-red-500/60 transition-all text-left group"
            data-testid="live-management-btn"
          >
            <div className="flex items-center gap-6">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-purple-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Radio className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground mb-1">Live & Podcast</h3>
                <p className="text-muted-foreground text-sm">
                  Manage live streams, podcasts, and chat
                </p>
              </div>
            </div>
          </button>
        </motion.div>

        {/* Memberships & Courses Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.93 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/admin/memberships')}
            className="w-full bg-gradient-to-r from-green-500/20 to-teal-500/20 border-2 border-green-500/30 rounded-2xl p-6 hover:border-green-500/60 transition-all text-left group"
            data-testid="memberships-management-btn"
          >
            <div className="flex items-center gap-6">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground mb-1">Memberships & Courses</h3>
                <p className="text-muted-foreground text-sm">
                  Manage subscribers, course enrollments, and certificates
                </p>
              </div>
            </div>
          </button>
        </motion.div>

        {/* User Management (Admin only) */}
        {isAdmin() && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95 }}
            className="mb-8"
          >
            <button
              onClick={() => navigate('/admin/users')}
              className="w-full bg-gradient-to-r from-red-500/20 to-orange-500/20 border-2 border-red-500/30 rounded-2xl p-6 hover:border-red-500/60 transition-all text-left group"
              data-testid="user-management-btn"
            >
              <div className="flex items-center gap-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <UserCog className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-1">User & Membership Management</h3>
                  <p className="text-muted-foreground text-sm">
                    Add admins, editors, members, and customers • Manage roles and memberships
                  </p>
                </div>
              </div>
            </button>
          </motion.div>
        )}

        {/* Theme Customizer Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/admin/theme')}
            className="w-full bg-gradient-to-r from-primary/20 to-accent/20 border-2 border-primary/30 rounded-2xl p-8 hover:border-primary/60 transition-all text-left group"
          >
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Palette className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Theme Customizer</h3>
                <p className="text-muted-foreground">
                  Change colors, fonts, and style to match your brand
                </p>
              </div>
            </div>
          </button>
        </motion.div>

        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="bg-card border border-border/50 rounded-2xl p-6"
        >
          <h2 className="text-2xl font-bold font-display text-foreground mb-6">
            Recent Orders
          </h2>
          {stats?.recent_orders && stats.recent_orders.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_orders.slice(0, 5).map((order, index) => (
                <div
                  key={order.id || index}
                  className="flex items-center justify-between p-4 bg-background rounded-xl border border-border/30"
                >
                  <div>
                    <p className="text-foreground font-semibold">{order.email}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(order.purchased_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-primary font-bold">${order.price.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">{order.item_type}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No orders yet</p>
          )}
        </motion.div>
      </main>
    </div>
  );
};

// ============================================================
// Test Notification Card — one-click purchase-notification test
// Lives in the AdminDashboard so Curtis can verify emails
// without needing to actually pay for anything.
// ============================================================
const TestNotificationCard = () => {
  const [buyerEmail, setBuyerEmail] = useState('casasambacurtis@gmail.com');
  const [buyerName, setBuyerName] = useState('Curtis Pierre (test)');
  const [itemName, setItemName] = useState('Test Class Purchase');
  const [price, setPrice] = useState('10.00');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const fireTest = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/test/send-purchase-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          buyer_email: buyerEmail.trim(),
          buyer_name: buyerName.trim(),
          item_name: itemName.trim(),
          price: parseFloat(price) || 0,
          item_type: 'Test Purchase',
        }),
      });
      const data = await res.json();
      setLastResult(data);
      if (data?.detail?.admin_sent) {
        toast.success('Owner inbox notified — check casasambacurtis@gmail.com');
      } else {
        toast.error(`Owner send failed: ${data?.detail?.admin_error || 'unknown error'}`);
      }
    } catch (e) {
      toast.error(`Request failed: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="mb-8"
    >
      <div className="bg-gradient-to-r from-amber-500/10 via-primary/5 to-amber-500/10 border-2 border-amber-500/30 rounded-2xl p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <Mail className="w-6 h-6 text-amber-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-foreground mb-1">Test Purchase Notification</h3>
            <p className="text-sm text-muted-foreground">
              Fire a fake purchase to prove the email pipeline works — no charge, no Stripe, nothing to refund.
              An email lands in <span className="font-mono text-amber-500">casasambacurtis@gmail.com</span> immediately.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <input
            type="email"
            value={buyerEmail}
            onChange={(e) => setBuyerEmail(e.target.value)}
            placeholder="buyer email"
            className="px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground"
            data-testid="test-notif-buyer-email"
          />
          <input
            type="text"
            value={buyerName}
            onChange={(e) => setBuyerName(e.target.value)}
            placeholder="buyer name"
            className="px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground"
            data-testid="test-notif-buyer-name"
          />
          <input
            type="text"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            placeholder="item name"
            className="px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground"
            data-testid="test-notif-item-name"
          />
          <input
            type="number"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="price"
            className="px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground"
            data-testid="test-notif-price"
          />
        </div>

        <button
          onClick={fireTest}
          disabled={sending || !buyerEmail}
          className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="test-notif-send-btn"
        >
          {sending ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
          ) : (
            <><Send className="w-4 h-4" /> Send Test Notification</>
          )}
        </button>

        {lastResult && (
          <div className="mt-4 p-3 rounded-lg bg-background/60 border border-border text-xs font-mono" data-testid="test-notif-result">
            <div className="flex items-center gap-2 mb-1">
              {lastResult?.detail?.admin_sent ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-foreground">
                Owner inbox: {lastResult?.detail?.admin_sent ? 'DELIVERED' : 'FAILED'}
              </span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              {lastResult?.detail?.buyer_sent ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-amber-500" />
              )}
              <span className="text-foreground">
                Buyer inbox: {lastResult?.detail?.buyer_sent ? 'DELIVERED' : 'SKIPPED (Resend sandbox — verify a domain to enable)'}
              </span>
            </div>
            {lastResult?.detail?.admin_error && (
              <div className="text-red-400 mt-2">Owner error: {lastResult.detail.admin_error}</div>
            )}
            {lastResult?.detail?.buyer_error && (
              <div className="text-amber-400 mt-2">Buyer error: {lastResult.detail.buyer_error}</div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};
